import { type JSX, Show } from "solid-js";
import { Dynamic } from "solid-js/web";
import { Link } from "@tanstack/solid-router";
import { AppBackground } from "@/components/app-background";

/**
 * Daily Journal's OWN auth shell — deliberately NOT the shared `AuthLayout`
 * (the 4-variant glass-card-in-a-split-panel frame every clone inherited, and
 * which therefore read as a clone no matter how the brand copy changed).
 *
 * Daily Journal's identity is a **private page in a notebook**, so its auth is
 * an editorial *letter on a ruled sheet* — not a card, not a split, not a hero
 * banner. The form sits full-bleed on a faint ruled-paper backdrop with a serif
 * masthead above it: the open-notebook mark, a serif display headline, and a
 * per-screen eyebrow. A single hairline (the margin rule of the page) separates
 * the masthead from the writing area below.
 *
 *   ┌──────────────────────────────────┐
 *   │  ◳ Journal            · eyebrow · │  ← masthead: notebook chip + wordmark
 *   │                                   │
 *   │  Welcome back to your pages.      │  ← serif display headline (the h1)
 *   │  Supporting line in serif.        │
 *   │ ───────────────────────────────── │  ← margin hairline (no card border)
 *   │  email   [____________________]   │  ← form FULL-BLEED on the ruled sheet
 *   │  pass    [____________________]   │     (no GlassSurface frame at all)
 *   │  [ Sign in ]                      │
 *   └──────────────────────────────────┘
 *
 * No glass surface, no card chrome, no two-column split: the form lives directly
 * on a ruled-paper sheet with its own serif masthead, so Daily Journal looks
 * structurally unlike the other products even though they share the same
 * primitives (buttons, fields, SocialAuthButtons, disabled-until-ready). The
 * shell is agnostic about the *form* — screens pour their fields in as children.
 *
 * ── SCROLL CONTRACT (#13, same fix as the kit / melodimals #4) ──────────────
 * `AppBackground` is `position: fixed; overflow: hidden` and its content slot is
 * `h-full`, so anything rendered INSIDE it is clipped and cannot scroll.
 *
 * In THIS clone the root shell (`__root.tsx`) ALREADY wraps every route —
 * including auth — inside a fixed/overflow-hidden `AppBackground`. So unlike the
 * bare kit (where `AppBackground` lives only in the auth layout and a
 * `min-h-[100dvh]` sibling container suffices to grow past the viewport), here
 * the auth content is nested INSIDE a fixed-height, overflow-hidden ancestor
 * that clips at the viewport edge. A `min-h-[100dvh]`-that-grows container would
 * therefore be clipped by that ancestor exactly like the original #6 bug.
 *
 * The fix that actually engages here — identical to how `_app`'s
 * `app-layout-content` scrolls inside the very same root shell — is to make THIS
 * the real scroller: cap it to the viewport (`h-[100dvh]` / `max-h-[100dvh]`) so
 * its OWN `overflow-y-auto` takes over when the form is taller than the screen,
 * instead of overflowing the clipping ancestor. The inner `min-h-full` column
 * centers when the form is short and grows when it is tall, so a tall form
 * (register) or a short viewport (portrait, low landscape) is always fully
 * reachable by scrolling — the submit is never clipped.
 *
 * (The local `<AppBackground/>` below is the decorative backdrop for the scene
 * itself; the root one behind it provides the same tokens, so they compose.)
 *
 * ACCESSIBILITY: exactly one `h1` per screen — the masthead headline IS the page
 * heading (pass `headingLevel="h2"` only if a screen owns its h1 elsewhere). The
 * notebook wordmark is a decorative <p>. The ruled-paper backdrop is aria-hidden.
 */

interface JournalAuthSceneProps {
  /** Eyebrow chip beside the wordmark (e.g. "Welcome back"). Optional. */
  eyebrow?: string;
  /** Page headline — the editorial promise for this screen. Optional. */
  headline?: string;
  /** Supporting line under the headline. */
  tagline?: string;
  /**
   * Heading level for the headline. The masthead headline IS the page h1 for
   * Daily Journal's auth (there is no card title). Pass "h2" only if the screen
   * owns its own h1 elsewhere.
   */
  headingLevel?: "h1" | "h2";
  /** Width of the writing column. "narrow" for login/forgot/reset/verify, "wide" for register. */
  width?: "narrow" | "wide";
  /** The form (fields + actions), rendered full-bleed below the masthead. */
  children: JSX.Element;
  /** Footer slot (links: register / back home), rendered under the form. */
  footer?: JSX.Element;
}

export function JournalAuthScene(props: JournalAuthSceneProps) {
  return (
    <>
      {/* Fixed decorative backdrop — NEVER the scroll parent (see contract). */}
      <AppBackground />

      {/* Real scroll container: a flex-child scroller inside the fixed root
          shell. Caps at the available height and scrolls internally when the
          form does not fit (portrait or low landscape). */}
      <div
        class="relative z-10 flex h-[100dvh] max-h-[100dvh] min-h-[100dvh] w-full flex-col overflow-x-hidden overflow-y-auto px-5 sm:px-6"
        style={{
          "padding-top": "max(2rem, env(safe-area-inset-top))",
          "padding-bottom": "max(2rem, env(safe-area-inset-bottom))",
          "padding-left": "max(1.25rem, env(safe-area-inset-left))",
          "padding-right": "max(1.25rem, env(safe-area-inset-right))",
        }}
      >
        <div
          class="relative mx-auto flex min-h-full w-full flex-col justify-center py-10"
          classList={{
            "max-w-sm": (props.width ?? "narrow") === "narrow",
            "max-w-md": props.width === "wide",
          }}
        >
          {/* Faint ruled-paper sheet behind the writing column — the page you
              are about to write on. Purely ornamental, theme-token only. */}
          <RuledSheet />

          {/* Masthead — notebook chip + serif wordmark + per-screen eyebrow. */}
          <header class="relative z-10 flex flex-col gap-6">
            <div class="flex items-center justify-between gap-3">
              <Link
                to="/"
                class="group flex items-center gap-3 rounded-2xl outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
                // oxlint-disable-next-line lingui/no-unlocalized-strings -- brand name, not UI copy
                aria-label="Daily Journal"
              >
                {/* Solid accent chip + open-notebook glyph: legible on any theme. */}
                <span
                  data-testid="brand-mark"
                  class="grid h-11 w-11 place-items-center rounded-2xl bg-accent text-accent-foreground shadow-sm ring-1 ring-black/5 transition-transform group-hover:scale-105 group-active:scale-95"
                >
                  <svg
                    class="h-6 w-6"
                    viewBox="0 0 32 32"
                    fill="none"
                    aria-hidden="true"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    {/* Open notebook: two mirrored pages with a central spine */}
                    <path
                      d="M16 9.4C13.6 7.8 10.6 7.1 7.4 7.4c-.8.1-1.4.8-1.4 1.6v12.7c0 .9.8 1.6 1.7 1.5 2.7-.3 5.4.3 7.6 1.7.4.3 1 .3 1.4 0 2.2-1.4 4.9-2 7.6-1.7.9.1 1.7-.6 1.7-1.5V9c0-.8-.6-1.5-1.4-1.6-3.2-.3-6.2.4-8.6 2z"
                      stroke="currentColor"
                      stroke-width="1.7"
                      stroke-linejoin="round"
                      stroke-linecap="round"
                    />
                    <path
                      d="M16 9.4v15.3"
                      stroke="currentColor"
                      stroke-width="1.7"
                      stroke-linecap="round"
                    />
                    {/* Pen signing the right page — distinctive stroke */}
                    <path
                      d="M22.6 12.2c-.4 2.6-1.7 4.4-3.9 5.4l-.7-.7c1-2.2 2.8-3.5 4.6-4.7z"
                      fill="currentColor"
                    />
                  </svg>
                </span>
                <span class="font-display text-2xl font-semibold leading-none tracking-tight text-foreground">
                  Journal
                </span>
              </Link>
              <Show when={props.eyebrow}>
                <span class="rounded-full bg-accent px-3 py-1 text-xs font-semibold text-accent-foreground shadow-sm ring-1 ring-black/5">
                  {props.eyebrow}
                </span>
              </Show>
            </div>

            <Show when={props.headline}>
              <div class="space-y-2.5">
                <Dynamic
                  component={(props.headingLevel ?? "h1") === "h2" ? "h2" : "h1"}
                  class="text-balance font-display text-3xl font-semibold tracking-tight text-foreground sm:text-4xl"
                >
                  {props.headline}
                </Dynamic>
                <Show when={props.tagline}>
                  <p class="text-pretty font-serif text-base leading-relaxed text-muted-foreground">
                    {props.tagline}
                  </p>
                </Show>
              </div>
            </Show>
          </header>

          {/* Margin hairline of the page — replaces the card border. */}
          <div class="relative z-10 my-7 h-px w-full bg-border/70" />

          {/* Form lives full-bleed on the ruled sheet (no glass card). */}
          <div class="relative z-10 w-full">{props.children}</div>

          <Show when={props.footer}>
            <footer class="relative z-10 mt-7 space-y-3 text-center">{props.footer}</footer>
          </Show>
        </div>
      </div>
    </>
  );
}

/**
 * Faint ruled-paper sheet behind the writing column: horizontal rules fading at
 * top and bottom, a soft accent halo, and a left margin line. Drawn entirely
 * with theme tokens (`--border`, accent) so it stays legible under every theme.
 * Purely ornamental and aria-hidden.
 */
function RuledSheet() {
  return (
    <div
      class="pointer-events-none absolute inset-x-0 inset-y-2 z-0 overflow-hidden"
      aria-hidden="true"
    >
      {/* Ruled lines, faded top and bottom */}
      <div
        class="absolute inset-0 opacity-[0.45]"
        style={{
          "background-image":
            "repeating-linear-gradient(to bottom, transparent 0, transparent 31px, var(--border) 31px, var(--border) 32px)",
          "mask-image":
            "linear-gradient(to bottom, transparent, black 14%, black 86%, transparent)",
        }}
      />
      {/* Left margin line — the notebook's red rule, drawn in accent. */}
      <div class="absolute inset-y-0 left-0 w-px bg-accent/25" />
      {/* Soft halo so the sheet reads as a page lifted off the background. */}
      <div class="absolute -right-20 -top-16 h-56 w-56 rounded-full bg-accent/10 blur-3xl" />
      <div class="absolute -bottom-20 -left-12 h-48 w-48 rounded-full bg-primary/10 blur-3xl" />
    </div>
  );
}
